import { useEffect, useRef } from "react";
import type { CustomSankeyLayerProps } from "@nivo/sankey";
import type {
  MoneyFlowLink,
  MoneyFlowNode,
  MoneyFlowSelection,
} from "./useMoneyFlowData";

/** Restore Nivo's native emphasis after hover ends, without changing selection. */
export function MoneyFlowSelectionLayer({
  selection,
  nodes,
  currentNode,
  currentLink,
  setCurrentNode,
  setCurrentLink,
}: CustomSankeyLayerProps<MoneyFlowNode, MoneyFlowLink> & {
  selection: MoneyFlowSelection | null;
}): null {
  const previousSelection = useRef(selection);

  useEffect(() => {
    const changed = previousSelection.current !== selection;
    previousSelection.current = selection;
    // Hovering the other kind of item doesn't clear Nivo's previous current
    // item. Remove our fallback so its node precedence can't mask a hovered band.
    if (!changed && currentNode && currentLink) {
      setCurrentNode(null);
      return;
    }
    // A live hover takes precedence until mouseleave clears Nivo's current item.
    if (!changed && (currentNode || currentLink)) return;

    const node = nodes.find((node) => node.id === selection) ?? null;
    if (currentNode !== node) setCurrentNode(node);
    if (currentLink) setCurrentLink(null);
  }, [
    selection,
    nodes,
    currentNode,
    currentLink,
    setCurrentNode,
    setCurrentLink,
  ]);

  return null;
}
